import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  DateField,
  FileUploader,
  Label,
  Price,
  Textarea,
} from '@hero/web-components';
import { fileToBase64 } from '@hero/web-utils';
import { CopyIcon, CheckIcon, FileTextIcon } from '@hero/web-icons';
import type { FinancingLenderView } from '@hero/api';

export type LenderFundingPanelProps = {
  view: FinancingLenderView;
  /**
   * Funding write callback. `fundingProof` is required at the UI
   * layer (we never let the lender stamp a funded event without
   * proof) even though the backend treats it as optional — the
   * server stays permissive so admin back-fills via `markOutcome`
   * can still proceed if the wire receipt is missing.
   */
  onConfirmFunding: (input: {
    at: Date;
    fundingProof: { upload: string };
    notes?: string;
  }) => Promise<void>;
};

/**
 * `LenderFundingPanel` — the lender's post-acceptance "make the
 * wire" surface on the public lender-view page.
 *
 * Mirrors the admin {@link RecordFundingDialog} but lives on the
 * lender's side of the wall:
 *
 *  - **Carrier bank** is rendered from `view.carrierBank`, the
 *    backend reveals it ONLY when this lender's offer has
 *    `decision: 'accepted'` (the projection layer enforces the
 *    privacy gate; we just trust the shape here).
 *  - **Locked terms** (advance, fee, term, recourse) come from the
 *    accepted `myOffer.terms` — exactly the figures the carrier
 *    agreed to. No editing here; if those are wrong the lender
 *    contacts ops, who fixes them on the admin side before the
 *    carrier accepts again.
 *  - **Operational inputs** the lender controls: `fundedOn` (date
 *    picker, defaults to today; backdating allowed for a wire that
 *    cleared the bank yesterday), optional `fundingProof` upload
 *    (PDF/JPG/PNG), optional notes.
 *
 * Visibility (computed by `FinancingLenderViewPage`):
 *  - `myOffer.decision === 'accepted'` &&
 *  - `application.status === 'accepted'` — the carrier picked this
 *    lender but no funding has been stamped yet. Once the backend
 *    flips `status` to `'funded'`, the page swaps this panel for
 *    the read-only "you funded this on X" banner.
 */
export function LenderFundingPanel({
  view,
  onConfirmFunding,
}: LenderFundingPanelProps) {
  const { t } = useTranslation('m-financing');
  const { application, carrierBank } = view;
  const { myOffer, invoice } = application;
  const terms = myOffer.terms;

  const [fundedOn, setFundedOn] = useState<Date | undefined>(() => new Date());
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Required to submit: funding date, funding proof file, and the
   * accuracy acknowledgement. We enforce all three at the UI layer
   * because once the lender stamps `funded` the application leaves
   * this surface — there's no "undo" affordance, so the gates here
   * stand in for the manual review an operator would otherwise do.
   * The backend additionally enforces the `decision === 'accepted'`
   * + `status === 'accepted'` invariants (see
   * `lenderMarkFundedAPI`).
   */
  const canSubmit =
    Boolean(fundedOn) &&
    Boolean(proofFile) &&
    acknowledged &&
    !submitting;

  const handleSubmit = async () => {
    if (!fundedOn || !proofFile) return;
    setSubmitting(true);
    try {
      const fundingProof = { upload: await fileToBase64(proofFile) };
      await onConfirmFunding({
        at: fundedOn,
        fundingProof,
        notes: notes.trim() ? notes.trim() : undefined,
      });
      toast.success(t('partner_page.funding.toasts.success'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('partner_page.funding.toasts.failed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm sm:p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-900">
          {t('partner_page.funding.title')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('partner_page.funding.description')}
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <BankDetailsCard
          carrierBank={carrierBank}
          carrierName={view.org.name}
        />
        <LockedTermsCard
          terms={terms}
          currency={invoice.currency}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          label={t('partner_page.funding.fields.funded_on.title')}
          required
          value={fundedOn}
          onChange={setFundedOn}
        />
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-1">
            {t('partner_page.funding.fields.proof.title')}
            <span className="text-red-600" aria-hidden="true">
              *
            </span>
          </Label>
          <FileUploader
            files={proofFile ? [proofFile] : []}
            onChange={files => setProofFile(files[0] ?? null)}
            label={t('partner_page.funding.fields.proof.label')}
            variant="compact"
            acceptedFileTypes={{
              'application/pdf': [],
              'image/png': [],
              'image/jpeg': [],
            }}
            hint={t('partner_page.funding.fields.proof.hint')}
          />
          {!proofFile && (
            <p className="text-xs text-amber-700">
              {t('partner_page.funding.fields.proof.required_hint')}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>{t('partner_page.funding.fields.notes.title')}</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('partner_page.funding.fields.notes.placeholder')}
          />
        </div>
      </div>

      {/**
       * Accuracy acknowledgement. Required gate before the submit
       * button becomes clickable — keeps the lender deliberate about
       * the wire / contract / repayment terms instead of clicking
       * "Confirm" on autopilot. Mirrors the carrier-side accept-offer
       * checkbox pattern.
       */}
      <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
        <Checkbox
          className="mt-0.5"
          checked={acknowledged}
          onCheckedChange={value => setAcknowledged(value === true)}
        />
        <span className="text-sm text-gray-700">
          {t('partner_page.funding.fields.acknowledge.label')}
        </span>
      </label>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="gap-2">
          {submitting ? (
            t('partner_page.funding.actions.confirming')
          ) : (
            <>
              {t('partner_page.funding.actions.confirm')}
              {terms && (
                <Price
                  amount={terms.advancedAmount}
                  currency={invoice.currency}
                  size="sm"
                  className="font-semibold"
                />
              )}
            </>
          )}
        </Button>
      </div>
    </section>
  );
}

/**
 * Read-only summary the lender renders after the funding has been
 * stamped (i.e. `application.status === 'funded'`). The lender-view
 * projection doesn't expose the outcome timestamp itself — we just
 * confirm the operational write landed and surface the locked
 * advance so the lender sees the figure they just disbursed.
 */
export function LenderFundingRecordedCard({
  view,
}: {
  view: FinancingLenderView;
}) {
  const { t } = useTranslation('m-financing');
  const { application } = view;
  const terms = application.myOffer.terms;
  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm sm:p-6">
      <div className="flex items-center gap-2">
        <CheckIcon className="h-5 w-5 text-emerald-600" />
        <h2 className="text-sm font-semibold text-gray-900">
          {t('partner_page.funding.recorded.title')}
        </h2>
      </div>
      <p className="mt-1 text-sm text-gray-700">
        {t('partner_page.funding.recorded.description')}
      </p>
      {terms && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm">
          <span className="text-gray-500">
            {t('partner_page.funding.terms.advance')}
          </span>
          <Price
            amount={terms.advancedAmount}
            currency={application.invoice.currency}
            size="sm"
            className="font-semibold text-emerald-700"
          />
        </div>
      )}
    </section>
  );
}

/**
 * Friendly bank labels — keyed by the `bankNameSchema` enum slugs in
 * `@hero/schema/common/bank.schema`. We render these inline (rather
 * than reaching for a translation lookup) so the lender-view page
 * stays self-contained and doesn't fail to render if a stray slug
 * lands here without a matching translation key.
 */
const BANK_LABEL: Record<string, string> = {
  alrajhi: 'Al Rajhi Bank',
  alahli: 'Al Ahli Bank (SNB)',
  alinma: 'Alinma Bank',
  albilad: 'Al Bilad Bank',
  arab_national: 'Arab National Bank',
  riyad: 'Riyad Bank',
  saudi_awwal: 'Saudi Awwal Bank',
  saudi_fransi: 'Banque Saudi Fransi',
};

function BankDetailsCard({
  carrierBank,
  carrierName,
}: {
  carrierBank: FinancingLenderView['carrierBank'];
  carrierName: string;
}) {
  const { t } = useTranslation('m-financing');
  if (!carrierBank) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
        <h3 className="text-sm font-semibold text-amber-900">
          {t('partner_page.funding.bank.empty_title')}
        </h3>
        <p className="text-xs text-amber-800">
          {t('partner_page.funding.bank.empty_description', {
            carrier: carrierName,
          })}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">
        {t('partner_page.funding.bank.title')}
      </h3>
      <dl className="grid grid-cols-1 gap-2 text-sm">
        <BankRow
          label={t('partner_page.funding.bank.bank_name')}
          value={BANK_LABEL[carrierBank.bankName] ?? carrierBank.bankName}
        />
        <BankRow
          label={t('partner_page.funding.bank.account_name')}
          value={carrierBank.accountName}
        />
        <BankRow
          label={t('partner_page.funding.bank.iban')}
          value={carrierBank.IBAN}
          copyable
          mono
        />
      </dl>
    </div>
  );
}

function BankRow({
  label,
  value,
  copyable,
  mono,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // No-op: clipboard write can fail in non-secure contexts;
      // the user can still select the value manually.
    }
  };
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 pb-2 last:border-0 last:pb-0">
      <dt className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd
        className={`flex items-center justify-between gap-2 text-sm text-gray-900 ${
          mono ? 'font-mono' : 'font-medium'
        }`}>
        <span className="break-all">{value}</span>
        {copyable && (
          <button
            type="button"
            onClick={handleCopy}
            className="flex flex-none items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
            aria-label="Copy">
            {copied ? (
              <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <CopyIcon className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </dd>
    </div>
  );
}

function LockedTermsCard({
  terms,
  currency,
}: {
  terms: FinancingLenderView['application']['myOffer']['terms'];
  currency: string;
}) {
  const { t, i18n } = useTranslation('m-financing');
  if (!terms) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
        <FileTextIcon className="h-4 w-4 text-gray-400" />
        <p className="text-xs text-gray-500">
          {t('partner_page.funding.terms.no_terms')}
        </p>
      </div>
    );
  }
  /**
   * Total the carrier will repay the lender = advance + fee. This
   * is the headline figure for the lender ("you'll wire X today and
   * collect Y on the repayment date") — surface it as a separate
   * highlighted row next to the advance so the gross margin is
   * immediately visible.
   */
  const repaymentTotal = terms.advancedAmount + terms.feeAmount;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">
        {t('partner_page.funding.terms.title')}
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <TermRow
          label={t('partner_page.funding.terms.advance')}
          value={
            <Price
              amount={terms.advancedAmount}
              currency={currency}
              size="sm"
              className="font-semibold text-emerald-700"
            />
          }
          highlight
        />
        <TermRow
          label={t('partner_page.funding.terms.repayment_total')}
          value={
            <Price
              amount={repaymentTotal}
              currency={currency}
              size="sm"
              className="font-semibold text-emerald-700"
            />
          }
          highlight
        />
        <TermRow
          label={t('partner_page.funding.terms.fee')}
          value={
            <span>
              <Price
                amount={terms.feeAmount}
                currency={currency}
                size="sm"
                className="font-medium"
              />
              <span className="ms-1 text-gray-500">
                ({(terms.feePercentage * 100).toFixed(1)}%)
              </span>
            </span>
          }
        />
        <TermRow
          label={t('partner_page.funding.terms.term')}
          value={`${terms.term.value} ${terms.term.type}`}
        />
        <TermRow
          label={t('partner_page.funding.terms.repayment_date')}
          value={formatDate(terms.repaymentDate, i18n.language)}
        />
        <TermRow
          label={t('partner_page.funding.terms.recourse')}
          value={
            terms.recourse
              ? t('partner_page.funding.terms.recourse_yes')
              : t('partner_page.funding.terms.recourse_no')
          }
        />
      </dl>
    </div>
  );
}

/**
 * Format a Date/ISO string as a short calendar date in the active
 * locale. Returns the original value if it isn't a parseable date
 * (defensive — the schema guarantees a Date but TanStack hydration
 * from `localStorage` can deserialise it as a string).
 */
function formatDate(value: Date | string | undefined, lang: string): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function TermRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-0.5 ${
        highlight ? 'col-span-2 rounded-md bg-emerald-50 px-2 py-1.5' : ''
      }`}>
      <dt className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}
